/**
 * A porta unica do modulo 01 para o backend.
 *
 * ## O erro carrega as duas metades ate a tela
 *
 * A regra 10 do checklist (p.25) manda a mensagem de erro dizer o que fazer em
 * seguida, nao so o que deu errado. Do lado do servidor isso ja e verdade: toda
 * recusa devolve `{ erro, comoResolver }`, inclusive as recusas do BANCO —
 * nome repetido, referencia a cadastro de outra empresa, campo obrigatorio
 * vazio — traduzidas em `http/erro-de-cadastro.ts`. Se o cliente jogasse isso
 * fora e mostrasse "Erro ao salvar", o trabalho do outro lado viraria enfeite.
 *
 * ## O 401 tem texto proprio, e ele nao e um defeito desta tela
 *
 * A autenticacao EXISTE desde a migracao 0009. Sem cookie valido, TODA rota de
 * dominio responde 401 — que e o comportamento CORRETO do backend. Deixar isso
 * chegar como "falha ao carregar" mandaria quem abre a tela procurar problema
 * no lugar errado; a frase diz o que fazer, que e entrar outra vez.
 *
 * PENDENCIA REGISTRADA: os modulos 01, 02 e 08 tem, cada um, o seu `api.ts`
 * com esta mesma funcao. Nasceram em frentes paralelas; juntar num `lib/api.ts`
 * e mecanico e e de quem integra.
 */

import { buscar } from '@/lib/licenca-das-rotas';
import { BASE_DA_API, CREDENCIAIS_DA_API } from '@/lib/api';

/**
 * A base e as credenciais vem de `@/lib/api`, num lugar so.
 *
 * Vazio quer dizer `/api`, que o proxy do Vite reescreve em
 * desenvolvimento; num container ela e o endereco completo da API, assado
 * no build por `VITE_API_URL`. Nenhum nome de maquina mora neste arquivo.
 */
const BASE = BASE_DA_API;

export class ErroDaApi extends Error {
  readonly status: number;
  readonly comoResolver: string;
  /** O codigo da recusa, quando o servidor o manda (`ficha_alterada`, `situacao_mudou`). */
  readonly codigo: string | null;

  constructor(status: number, mensagem: string, comoResolver: string, codigo: string | null = null) {
    super(mensagem);
    this.name = 'ErroDaApi';
    this.status = status;
    this.comoResolver = comoResolver;
    this.codigo = codigo;
  }
}

/**
 * A frase de 401, num lugar so.
 *
 * Ela ja afirmou que "a autenticacao ainda nao foi construida (historia
 * F-04)", e isso deixou de ser verdade na migracao 0009: `POST /sessao`
 * existe, confere a senha dentro do banco e abre sessao com prazo. A tela
 * desmentia o proprio botao "Ir para o login", que ficava logo abaixo dela.
 *
 * Um 401 hoje quer dizer UMA coisa: nao ha sessao valida neste navegador —
 * porque ninguem entrou, ou porque as 12 horas do prazo passaram com a aba
 * aberta (o cliente de consulta nao repergunta ao voltar o foco).
 */
export const SEM_SESSAO =
  'Sua sessão não está aberta, ou expirou. Entre outra vez para ver os cadastros.';

interface CorpoDeErro {
  readonly erro?: string;
  readonly comoResolver?: string;
  readonly codigo?: unknown;
}

async function lerCorpo(resposta: Response): Promise<unknown> {
  const texto = await resposta.text();
  if (!texto) return null;
  try {
    return JSON.parse(texto) as unknown;
  } catch {
    return texto;
  }
}

/**
 * `credentials` nao e detalhe: o token de sessao e um cookie opaco e
 * `HttpOnly`, e sem isto ele nao viaja. Vale `'include'` (ver `@/lib/api`)
 * porque a API passou a morar noutra maquina, e `'same-origin'` o omitiria
 * em silencio — toda rota responderia 401 sem nada parecer errado no
 * servidor.
 */
export async function pedir<T>(caminho: string, opcoes: RequestInit = {}): Promise<T> {
  let resposta: Response;
  try {
    /* A leitura sem a licenca da rota nem sai: ver `@/lib/licenca-das-rotas`. */
    resposta = await buscar(`${BASE}${caminho}`, caminho, {
      credentials: CREDENCIAIS_DA_API,
      headers: opcoes.body ? { 'content-type': 'application/json' } : {},
      ...opcoes,
    });
  } catch {
    throw new ErroDaApi(
      0,
      'O sistema não respondeu',
      'Confira se o backend está no ar e tente carregar de novo; se continuar, avise o suporte com o horário.',
    );
  }

  const corpo = await lerCorpo(resposta);

  if (!resposta.ok) {
    if (resposta.status === 401) {
      throw new ErroDaApi(401, 'Sua sessão não está aberta', SEM_SESSAO);
    }
    const detalhe = (typeof corpo === 'object' && corpo !== null ? corpo : {}) as CorpoDeErro;
    throw new ErroDaApi(
      resposta.status,
      detalhe.erro ?? 'Não foi possível concluir',
      detalhe.comoResolver ?? 'Confira os dados informados e tente de novo.',
      typeof detalhe.codigo === 'string' ? detalhe.codigo : null,
    );
  }

  return corpo as T;
}

export function enviarJson<T>(caminho: string, metodo: 'POST' | 'PUT' | 'PATCH' | 'DELETE', corpo: unknown): Promise<T> {
  return pedir<T>(caminho, { method: metodo, body: JSON.stringify(corpo) });
}

/** Instante do banco, que chega em ISO 8601, ou nulo. */
export function paraInstante(valor: unknown): Date | null {
  if (typeof valor !== 'string' || !valor) return null;
  const quando = new Date(valor);
  return Number.isNaN(quando.getTime()) ? null : quando;
}

/**
 * `numeric` do Postgres chega como TEXTO no driver `pg`, e de proposito: em
 * `double` um limite de alcada perde centavo.
 */
export function paraNumero(valor: unknown): number | null {
  if (valor === null || valor === undefined || valor === '') return null;
  const n = typeof valor === 'number' ? valor : Number(valor);
  return Number.isFinite(n) ? n : null;
}
